
import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { MessageSquarePlus, HelpCircle, Search, Lightbulb, Zap, PlusCircle, Sparkles } from 'lucide-react';
import { cn } from '@/lib/utils';
import { motion, AnimatePresence } from 'framer-motion';
import { TopicType } from '@/types/assistant';
import { TOPIC_CONFIG } from '@/contexts/AiAssistantContext';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';

interface SuggestionButtonsProps {
  suggestions: string[];
  topic: TopicType;
  onSuggestionClick: (suggestion: string) => void; 
  mode?: 'expert' | 'conversation';
}

export const SuggestionButtons: React.FC<SuggestionButtonsProps> = React.memo(({
  suggestions,
  topic,
  onSuggestionClick,
  mode = 'expert'
}) => {
  const [hoveredIndex, setHoveredIndex] = useState<number | null>(null);
  
  if (mode === 'conversation') {
    return null;
  }
  
  if (!suggestions || !Array.isArray(suggestions) || suggestions.length === 0) {
    return null;
  }

  const topicConfig = TOPIC_CONFIG[topic];
  
  const getSuggestionIcon = (suggestion: string) => {
    if (suggestion.toLowerCase().includes('how') || suggestion.toLowerCase().includes('explain')) {
      return <HelpCircle className="h-3 w-3 shrink-0" />;
    } else if (suggestion.toLowerCase().includes('what') || suggestion.toLowerCase().includes('find')) {
      return <Search className="h-3 w-3 shrink-0" />;
    } else if (suggestion.toLowerCase().includes('more') || suggestion.toLowerCase().includes('tell')) {
      return <PlusCircle className="h-3 w-3 shrink-0" />;
    } else if (suggestion.toLowerCase().includes('best') || suggestion.toLowerCase().includes('tip')) {
      return <Lightbulb className="h-3 w-3 shrink-0" />;
    } else if (suggestion.toLowerCase().includes('improve') || suggestion.toLowerCase().includes('optimize')) {
      return <Zap className="h-3 w-3 shrink-0" />;
    } else {
      return <MessageSquarePlus className="h-3 w-3 shrink-0" />;
    }
  };

  const getTruncatedText = (text: string, maxLength: number = 28) => {
    return text.length > maxLength ? `${text.substring(0, maxLength)}...` : text;
  };

  return (
    <motion.div 
      className="flex flex-wrap gap-2 mt-3"
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3, ease: "easeOut" }}
      key={`suggestions-container-${topic}`}
    >
      <AnimatePresence>
        {suggestions.slice(0, 3).map((suggestion, index) => {
          const baseColor = topicConfig.color.replace('text-', '');
          const isHovered = hoveredIndex === index;
          const truncatedText = getTruncatedText(suggestion);

          return (
            <motion.div 
              key={`suggestion-${index}-${topic}`}
              initial={{ opacity: 0, scale: 0.8, y: 10 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.8, y: -10 }}
              transition={{ 
                duration: 0.3, 
                delay: 0.1 * index,
                type: "spring",
                stiffness: 300,
                damping: 25
              }}
              onHoverStart={() => setHoveredIndex(index)}
              onHoverEnd={() => setHoveredIndex(null)}
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
            >
              <Tooltip delayDuration={200}>
                <TooltipTrigger asChild>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => onSuggestionClick(suggestion)}
                    className={cn(
                      "group relative h-7 px-3 py-1 rounded-full text-xs flex items-center gap-2",
                      "backdrop-blur-md border-2 transition-all duration-300",
                      "shadow-md hover:shadow-lg leading-none",
                      `border-${baseColor}/30 text-${baseColor} bg-gradient-to-r from-${baseColor}/10 to-${baseColor}/5`,
                      isHovered && [
                        `border-${baseColor}/50 bg-gradient-to-r from-${baseColor}/20 to-${baseColor}/10`,
                        "shadow-xl transform-gpu"
                      ],
                      `hover:shadow-${baseColor}/20`
                    )}
                  >
                    <motion.div
                      className={cn(
                        "absolute inset-0 rounded-full opacity-0 transition-opacity duration-300",
                        `bg-gradient-to-r from-${baseColor}/20 to-transparent`
                      )}
                      animate={{ opacity: isHovered ? 1 : 0 }}
                    />
                    
                    <motion.div 
                      className={cn(
                        "relative z-10 p-0.5 rounded-full transition-all duration-200",
                        `bg-${baseColor}/15`,
                        isHovered && `bg-${baseColor}/25`
                      )}
                      animate={{ 
                        rotate: isHovered ? [0, -10, 10, 0] : 0,
                        scale: isHovered ? 1.1 : 1
                      }}
                      transition={{ duration: 0.4, ease: "easeInOut" }}
                    >
                      {getSuggestionIcon(suggestion)}
                    </motion.div>
                    
                    <motion.span 
                      className="relative z-10 truncate max-w-28 font-medium leading-none"
                      animate={{ 
                        x: isHovered ? 2 : 0
                      }}
                      transition={{ duration: 0.2 }}
                    >
                      {truncatedText}
                    </motion.span>
                    
                    <AnimatePresence>
                      {isHovered && (
                        <motion.div
                          className="absolute -top-0.5 -right-0.5 z-20"
                          initial={{ opacity: 0, scale: 0 }}
                          animate={{ opacity: 1, scale: 1 }}
                          exit={{ opacity: 0, scale: 0 }}
                          transition={{ duration: 0.2 }}
                        >
                          <Sparkles className={cn("h-2.5 w-2.5", `text-${baseColor}`)} />
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </Button>
                </TooltipTrigger>
                <TooltipContent 
                  side="top"
                  className={cn(
                    "max-w-xs bg-background/95 backdrop-blur-sm border-2",
                    "text-xs py-2 px-3 break-words rounded-lg shadow-xl",
                    "z-50 leading-[1.4]",
                    `border-${baseColor}/30`
                  )}
                >
                  <div className="flex items-start gap-2">
                    <div className={cn(`text-${baseColor}`)}>
                      {getSuggestionIcon(suggestion)}
                    </div>
                    <span className={cn(`text-${baseColor}`)}>{suggestion}</span>
                  </div>
                </TooltipContent>
              </Tooltip>
            </motion.div>
          );
        })}
      </AnimatePresence>
    </motion.div>
  );
}, (prevProps, nextProps) => {
  return prevProps.topic === nextProps.topic && 
         prevProps.mode === nextProps.mode &&
         JSON.stringify(prevProps.suggestions) === JSON.stringify(nextProps.suggestions);
});

SuggestionButtons.displayName = "SuggestionButtons";
